import { useQueryStates, parseAsArrayOf, parseAsString, parseAsInteger } from 'nuqs';
import { useCallback } from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useFilterLabels } from './useFilterLabels';

/**
 * Generic hook for managing filter state synchronized with URL using nuqs
 * This hook provides URL synchronization for any filter configuration
 * 
 * @param {string[]} filterKeys - Array of filter field names to sync with URL
 * @param {Object} options - Configuration options
 * @param {Object} options.defaultPagination - Default pagination values
 * @param {number} options.throttleMs - Throttle URL updates (default: 300ms)
 * @param {boolean} options.clearOnDefault - Clear URL params when values are default
 * @returns {Object} Filter state and update functions
 */
export const useURLFilterState = (
  filterKeys, 
  options = {}
) => {
  const {
    defaultPagination = { page: 1, size: 20 },
    throttleMs = 300,
    clearOnDefault = true
  } = options;

  const { t } = useTranslation();

  if (!filterKeys || !Array.isArray(filterKeys)) {
    throw new Error('filterKeys is required and must be an array');
  }

  // Create parsers for each filter key (arrays of strings)
  const filterParsers = filterKeys.reduce((acc, key) => {
    acc[key] = parseAsArrayOf(parseAsString).withDefault([]);
    return acc;
  }, {});

  // Add pagination and sorting parsers
  const allParsers = {
    ...filterParsers,
    page: parseAsInteger.withDefault(defaultPagination.page),
    size: parseAsInteger.withDefault(defaultPagination.size),
    sort_by: parseAsString.withDefault(''),
    sort_order: parseAsString.withDefault(''),
  };

  // Use nuqs for URL state management
  const [filters, setFilters] = useQueryStates(allParsers, {
    history: 'replace', // Don't create new history entries
    shallow: false,     // Update URL immediately
    throttleMs,         // Throttle updates to prevent spam
    clearOnDefault,     // Clean URL when values are defaults
  });

  // Filter labels for display purposes
  const { setFilterLabel, getFilterLabel, clearFilterLabels } = useFilterLabels();

  /**
   * Updates a specific filter and resets pagination to page 1
   * @param {string} key - Filter key to update
   * @param {any} value - New filter value (will be converted to array)
   */
  const updateFilter = useCallback((key, value) => {
    setFilters({
      [key]: Array.isArray(value) ? value : [value].filter(Boolean),
      page: defaultPagination.page, // Reset to first page
    });
  }, [setFilters, defaultPagination.page]);

  /**
   * Updates filter with associated label data for display purposes
   * @param {string} key - Filter key to update  
   * @param {Array} value - Array of filter values
   * @param {Array} labelData - Array of label objects with label property
   */
  const updateFilterWithLabels = useCallback((key, value, labelData) => {
    updateFilter(key, value);

    // Store labels for display
    if (Array.isArray(value) && Array.isArray(labelData)) {
      labelData.forEach((item, index) => {
        const filterValue = value[index];
        if (filterValue && item?.label) {
          setFilterLabel(key, filterValue, item.label);
        }
      });
    }
  }, [updateFilter, setFilterLabel]);

  /**
   * Updates only pagination without affecting filters
   * @param {Object} pagination - Pagination object with page/size properties
   */
  const updatePagination = useCallback((pagination) => {
    setFilters({
      page: pagination.page || defaultPagination.page,
      size: pagination.size || defaultPagination.size,
    });
  }, [setFilters, defaultPagination]);

  /**
   * Updates sorting parameters and resets pagination to page 1
   * @param {string} field - Field to sort by (will have .keyword appended)
   * @param {string} direction - 'asc' or 'desc'
   */
  const updateSorting = useCallback((field, direction) => {
    const sortField = field ? `${field}.keyword` : '';
    setFilters({
      sort_by: sortField,
      sort_order: direction || '',
      page: defaultPagination.page, // Reset to first page when sorting changes
    });
  }, [setFilters, defaultPagination.page]);

  /**
   * Clears sorting and resets pagination to page 1
   */
  const clearSorting = useCallback(() => {
    setFilters({
      sort_by: '',
      sort_order: '',
      page: defaultPagination.page,
    });
  }, [setFilters, defaultPagination.page]);

  /**
   * Gets current sorting state in format expected by Antd Table
   * @returns {Object} Sorting object with field and order for Antd Table
   */
  const getCurrentSorting = useCallback(() => {
    const { sort_by, sort_order } = filters;
    if (!sort_by || !sort_order) return {};
    
    // Remove .keyword suffix for display
    const field = sort_by.replace('.keyword', '');
    return {
      field,
      order: sort_order === 'asc' ? 'ascend' : 'descend',
    };
  }, [filters]);

  /**
   * Resets all filters to empty arrays and pagination to defaults
   */
  const resetFilters = useCallback(() => {
    const resetState = filterKeys.reduce((acc, key) => {
      acc[key] = [];
      return acc;
    }, {});

    setFilters({
      ...resetState,
      page: defaultPagination.page,
      size: defaultPagination.size,
      sort_by: '',
      sort_order: '',
    });
    
    clearFilterLabels();
  }, [filterKeys, setFilters, defaultPagination, clearFilterLabels]);

  /**
   * Checks if any filters have active values (non-empty arrays)
   * @returns {boolean} True if any filter has values
   */
  const hasActiveFilters = useCallback(() => {
    return filterKeys.some((key) => filters[key]?.length > 0);
  }, [filters, filterKeys]);

  /**
   * Generates shareable URL with current filters
   * @returns {string} Complete URL with current filter state
   */
  const getShareableURL = useCallback(() => {
    return window.location.href; // nuqs already manages the URL
  }, []);

  /**
   * Copies current URL to clipboard for sharing with user feedback
   */
  const shareToClipboard = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(getShareableURL());
      message.success(t('common|Link copied to clipboard') || 'Link copied to clipboard');
      return true;
    } catch (error) {
      console.error('Failed to copy URL to clipboard:', error);
      message.error(t('common|Failed to copy link') || 'Failed to copy link');
      return false;
    }
  }, [getShareableURL, t]);

  return {
    filters,
    updateFilter,
    updateFilterWithLabels,
    updatePagination,
    updateSorting,
    clearSorting,
    getCurrentSorting,
    resetFilters,
    hasActiveFilters,
    getFilterLabel,
    getShareableURL,
    shareToClipboard,
  };
};